import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MomentDateAdapter } from '@angular/material-moment-adapter'
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core'
import { Subscription } from 'rxjs'
import { Utils } from '../../../../helpers/utils.helper'
import {
  IChoice,
  IKeyDownAction,
  IQuestion,
  IResponses,
} from '../../../../interfaces/question/question.interface'

import {
  FloatLabelType,
  MatFormFieldModule,
} from '@angular/material/form-field'
import * as _moment from 'moment'
import { environment } from 'src/environments/environment'
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { CommonModule } from '@angular/common'
import { TranslateModule } from '@ngx-translate/core'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatSelectModule } from '@angular/material/select'
import { MatIconModule } from '@angular/material/icon'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatInputModule } from '@angular/material/input'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { FormErrorsComponent } from './form-errors/form-errors.component'
import { MatButton } from '@angular/material/button'

const moment = _moment

const APP_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMM YYYY',
  },
}

@Component({
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    TranslateModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    NgxMaskDirective,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatInputModule,
    MatCheckboxModule,
    FormsModule,
    FormErrorsComponent,
    MatButton,
  ],
  selector: 'app-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
    provideNgxMask(),
  ],
})
export class FormComponent implements OnInit, OnChanges, OnDestroy {
  private formBuilder = inject(FormBuilder)
  private utils = inject(Utils)
  private destroyRef = inject(DestroyRef)

  @Input() questions!: IQuestion[]
  @Input() readonly: boolean = false
  @Input() key!: string
  @Input() dirtyFields: string[] = []
  @Output() responses = new EventEmitter<IResponses>()
  @Output() keyDownAction = new EventEmitter<IKeyDownAction>()
  @Output() iconClicked = new EventEmitter<IQuestion>()
  @Output() onBlurAction = new EventEmitter<IKeyDownAction>()

  form: FormGroup = this.formBuilder.group({})
  protected isProduction = environment.production
  protected isStaging = environment.staging
  formSubscription = new Subscription()
  emailValidation =
    /([A-z\d\.\+_-]+)@([A-z\d\.\+_-]+)\.([A-z]{2,4})(?:\.[A-z]{2,4})?/
  cpfValidation = /^\d{3}\.\d{3}\.\d{3}\-\d{2}$/

  constructor(dateAdapter: DateAdapter<Date>) {
    dateAdapter.setLocale('pt-BR')
  }

  ngOnInit(): void {
    this.form = this.toFormGroup(this.questions)
    this.watchFormResponses()
    this.emitReceivedFormResponses()
    this.setDirtyFields(this.dirtyFields)
  }

  ngOnChanges(): void {
    this.questions.some((question) => {
      if (question?.search) {
        this.buildQuestions()

        return true
      }
    })

    this.form = this.toFormGroup(this.questions)
    this.watchFormResponses()
    this.emitReceivedFormResponses()
    this.setDirtyFields(this.dirtyFields)
  }

  ngOnDestroy(): void {
    this.form.reset()
    this.formSubscription.unsubscribe()
  }

  getFloatLabel(question: IQuestion): FloatLabelType {
    return question?.floatLabel ? question?.floatLabel : 'auto'
  }

  public patchChoices(questionKey: string, choices: IChoice[]) {
    this.questions.find((question) => {
      if (question.key === questionKey) {
        question.choices = choices

        if (question?.search) {
          question.options = choices
        }

        return true
      }
    })
  }

  private buildQuestions() {
    this.questions = this.questions.map((question: IQuestion) => ({
      ...question,
      ...(question?.search &&
        question.type === 'select' &&
        question.choices?.length &&
        !question?.options?.length && { options: question.choices }),
    }))
  }

  public forceTest() {
    this.validateAllFormFields(this.form)
  }

  public isValidade() {
    this.validateAllFormFields(this.form)

    return this.form.valid
  }

  public setDisabledField(field: string, state: boolean) {
    if (state) {
      this.form.controls[field].disable()
    }

    if (!state) {
      this.form.controls[field].enable()
    }
  }

  public keyDownHandler(event: KeyboardEvent, question: IQuestion) {
    if (
      (event.code === '190' || event.key === '.') &&
      question.preventDotTypingInTextField
    ) {
      event.preventDefault()
    }

    if (event.code === 'Enter') {
      this.keyDownAction.emit({
        key: question.key,
        action: 'enter',
        value: this.form.get(question.key)?.value,
      })
    }

    if (question?.search) event.stopPropagation()

    if (
      event.key === 'Enter' &&
      question?.search &&
      question.type === 'select'
    ) {
      let field = this.questions.find((item) => item.key === question.key)

      if (field?.choices)
        this.form.get(question.key)?.patchValue(field?.choices[0].value)
    }
  }

  public keyDownHandlerConfirm(event: any) {
    if (event.code === 'Enter' || event.code === 'Tab') {
      event.target.blur()
    }
  }

  public onInputBlur(question: IQuestion) {
    this.onBlurAction.emit({
      key: question.key,
      action: 'blur',
      value: this.form.get(question.key)?.value,
    })
  }

  public selectOpenedHandler(question: IQuestion) {
    if (question.search) {
      document.getElementById(`${question.key}_search`)?.focus()
    }
  }

  public selectClosedHandler(question: IQuestion) {
    if (question.search) {
      this.form
        .get(`${question.key}_search`)
        ?.patchValue('', { emitEvent: false })
      this.filterSelectChoices(question.key, '')
    }
  }

  validateAllFormFields(formGroup: FormGroup) {
    //{1}
    Object.keys(formGroup.controls).forEach((field) => {
      //{2}
      const control = formGroup.get(field) //{3}
      if (control instanceof FormControl) {
        //{4}
        control.markAsTouched({ onlySelf: true })
      } else if (control instanceof FormGroup) {
        //{5}
        this.validateAllFormFields(control) //{6}
      }
    })
  }

  toFormGroup(questions: IQuestion[]): FormGroup {
    const group: any = {}

    const parsedQuestions = this.parseQuestions(questions)

    if (parsedQuestions) {
      parsedQuestions.forEach((question: IQuestion) => {
        const isFieldDisabled = this.readonly || question.isDisabled
        if (question.required) {
          group[question.key] = new FormControl(
            {
              value: question.value,
              disabled: isFieldDisabled!,
            },
            [
              Validators.required,
              question.isEmail ? Validators.email : Validators.nullValidator,
              question.pattern
                ? Validators.pattern(question.pattern)
                : Validators.nullValidator,
            ],
          )
          if (
            question.type === 'datepickerWithCheckbox' &&
            question.customCheckbox
          ) {
            group[question.customCheckbox.key] = new FormControl(
              {
                value: question.customCheckbox.value,
                disabled: question.customCheckbox.isDisabled,
              },
              [Validators.required],
            )
          }
        } else {
          group[question.key] = new FormControl(
            {
              value: question.value,
              disabled: isFieldDisabled!,
            },
            [
              question.isEmail ? Validators.email : Validators.nullValidator,
              question.pattern
                ? Validators.pattern(question.pattern)
                : Validators.nullValidator,
            ],
          )
          if (
            question.type === 'datepickerWithCheckbox' &&
            question.customCheckbox
          ) {
            group[question.customCheckbox.key] = new FormControl(
              {
                value: question.customCheckbox.value,
                disabled: question.customCheckbox.isDisabled,
              },
              [Validators.nullValidator],
            )
          }
        }

        if (question?.search && question.type === 'select') {
          group[`${question.key}_search`] = new FormControl()
          group[`${question.key}_search`].valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value: string) => {
              this.filterSelectChoices(question.key, value)
            })
        }
      })
      return new FormGroup(group)
    } else {
      return new FormGroup({})
    }
  }

  filterSelectChoices(questionKey: string, searchKey: string) {
    this.questions.find((item) => {
      if (item.key === questionKey) {
        if (item.options !== undefined && item.options.length) {
          item.choices = item.options?.filter((option) =>
            option.title.toLowerCase().includes(searchKey.toLowerCase()),
          )
        }

        return true
      }
    })
  }

  emitReceivedFormResponses(): void {
    this.responses.emit({
      responses: this.parseValuesFromObject(this.form.value),
      isFormValid: this.form.valid,
      key: this.key,
    })
  }

  watchFormResponses(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((formValue: any) => {
        const parsedResponses = this.parseResponses(formValue)
        this.responses.emit({
          responses: this.parseValuesFromObject(parsedResponses),
          isFormValid: this.form.valid,
          key: this.key,
        })
      })
  }

  togglePasswordInputType(question: IQuestion): void {
    const foundQuestionIndex = this.questions.findIndex(
      (q: IQuestion) => q.key === question.key,
    )
    if (foundQuestionIndex !== -1) {
      this.questions.splice(foundQuestionIndex, 1, {
        ...question,
        value: question.value ? question.value : '',
        questionType:
          question.questionType === 'password' ? 'text' : 'password',
      })
    }
  }

  onKeyPressCurrency(event: any): void {
    //only allow digits, dot, comma
    const pattern = /[0-9.,]/
    let inputChar = String.fromCharCode(event.charCode)
    if (!pattern.test(inputChar)) {
      event.preventDefault()
    }
  }

  setDirtyFields(fields: string[]): void {
    fields.forEach((field) => {
      this.form.get(field)?.markAsTouched()
    })
  }

  emitIconClicked(question: IQuestion): void {
    this.iconClicked.emit(question)
  }

  private parseValuesFromObject(object: any): any {
    Object.keys(object).map((key) => {
      if (
        key &&
        object[key] &&
        typeof object[key] === 'object' &&
        !Array.isArray(object[key])
      ) {
        object[key] = this.utils.spreadValueFromObject(object[key], 'value')
      }
    })

    return object
  }

  private parseResponses(formValue: any): any {
    this.questions.forEach((question: IQuestion) => {
      if (question.questionType === 'number') {
        formValue[question.key] = Number(formValue[question.key])
      }
    })
    return formValue
  }

  private parseQuestions(questions: IQuestion[]): IQuestion[] {
    if (questions) {
      return questions.map((question: IQuestion) => ({
        ...question,
        value:
          typeof question.value === 'boolean'
            ? question.value
            : question.value || '',
        required: question.required || false,
        isDisabled: question.isDisabled || false,
      }))
    } else {
      return []
    }
  }
}
